import * as Yup from 'yup';
import { Field, Formik } from 'formik';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import {
    IExtraCategoryOption,
    IExtraCategoryCreateInput,
    IExtraCategoryFragment,
    IExtraCategoryUpdateInput,
    IContentTypeValue,
    IFeature,
} from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import {
    ColorPicker,
    MultiSelect,
    Select,
    Switch,
    TextField,
} from 'common/components/FormField';
import { Button } from 'common/components/Button';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import {
    getExtraCategoryTypeTranslation,
    getExtraCategoryModelTypeTranslation,
} from 'organisation/utils/extraCategory';
import { Typography } from 'common/components/Typography';

import { ExtraCategoryChoiceOptions } from './ExtraCategoryChoiceOptions';

export type TExtraCategoryFormValues =
    | IExtraCategoryCreateInput
    | IExtraCategoryUpdateInput;

export interface IProps {
    disabled?: boolean;
    extraCategory?: IExtraCategoryFragment;
    initialValues:
        | IExtraCategoryCreateInput
        | (IExtraCategoryUpdateInput & {
              categoryType: IExtraCategoryCreateInput['categoryType'];
          });
    onClose?(): void;
    onDelete?(): void;
    onSubmit(values: TExtraCategoryFormValues): void;
}

export const ExtraCategoryForm = ({
    disabled,
    extraCategory,
    initialValues,
    onClose,
    onDelete,
    onSubmit,
}: IProps) => {
    const [translate] = useTranslation();

    // Budget requests only exist for organisations with the budget feature
    const { canUse: canUseBudget } = useFeature(IFeature.Budget);

    const modelTypeOptions = [
        IContentTypeValue.Group,
        IContentTypeValue.Offer,
        IContentTypeValue.Course,
        IContentTypeValue.Video,
        IContentTypeValue.Certificate,
        ...(canUseBudget ? [IContentTypeValue.BudgetRequest] : []),
    ];

    const validationSchema = Yup.object().shape({
        forModelTypes: Yup.array().min(1, translate('minOneOption')),
        name: Yup.string().required(translate('validate.required')),
        choiceOptions: Yup.array()
            .of(
                Yup.object().shape({
                    stringValue: Yup.string().required(
                        translate('validate.required')
                    ),
                })
            )
            .when('categoryType', {
                is: IExtraCategoryOption.Choice,
                then: (schema) => schema.min(1, translate('minOneOption')),
            }),
    });

    const buttonText = !!extraCategory
        ? translate('extraCategoriesDrawer.editButton')
        : translate('extraCategoriesDrawer.createButton');

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({
                    submitForm,
                    isSubmitting,
                    dirty,
                    values,
                    setFieldValue,
                }) => {
                    const isChoiceType =
                        values.categoryType === IExtraCategoryOption.Choice;
                    const isCheckboxType =
                        values.categoryType === IExtraCategoryOption.Boolean;
                    const isDateType =
                        values.categoryType === IExtraCategoryOption.Datetime;

                    const isTextType =
                        values.categoryType === IExtraCategoryOption.String;

                    return (
                        <Box sx={{ p: { xs: 2, sm: 4 } }}>
                            <Typography
                                sx={{ fontWeight: 'bold' }}
                                variant="h3"
                            >
                                {translate('extraCategoriesDrawer.formTitle')}
                            </Typography>
                            <Box sx={{ mb: 1 }}>
                                <Typography>
                                    {translate(
                                        'extraCategoriesDrawer.formText'
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 1, mb: 2 }}>
                                <Field
                                    component={MultiSelect}
                                    getOptionLabel={(
                                        option: IContentTypeValue
                                    ) =>
                                        getExtraCategoryModelTypeTranslation(
                                            option
                                        )
                                    }
                                    InputProps={{
                                        label: translate(
                                            'extraCategoriesDrawer.modelType'
                                        ),
                                    }}
                                    label="ModelType"
                                    name="forModelTypes"
                                    options={modelTypeOptions}
                                />

                                <Field
                                    component={Select}
                                    disabled={!!extraCategory}
                                    label={translate(
                                        'extraCategoriesDrawer.type'
                                    )}
                                    name="categoryType"
                                    options={[
                                        {
                                            label: getExtraCategoryTypeTranslation(
                                                IExtraCategoryOption.Boolean
                                            ),
                                            value: IExtraCategoryOption.Boolean,
                                        },
                                        {
                                            label: getExtraCategoryTypeTranslation(
                                                IExtraCategoryOption.Choice
                                            ),
                                            value: IExtraCategoryOption.Choice,
                                        },
                                        {
                                            label: getExtraCategoryTypeTranslation(
                                                IExtraCategoryOption.Datetime
                                            ),
                                            value: IExtraCategoryOption.Datetime,
                                        },
                                        {
                                            label: getExtraCategoryTypeTranslation(
                                                IExtraCategoryOption.String
                                            ),
                                            value: IExtraCategoryOption.String,
                                        },
                                    ]}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        // Clear choice options when switching
                                        // type, otherwise hidden options keep
                                        // failing required validation.
                                        setFieldValue(
                                            'categoryType',
                                            e.target.value
                                        );
                                        setFieldValue('choiceOptions', []);
                                    }}
                                />

                                <Divider sx={{ mt: 3, mb: 2 }} />

                                <Typography variant="h3">
                                    {translate(
                                        'extraCategoriesDrawer.settings'
                                    )}
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid size="grow">
                                        <Field
                                            component={TextField}
                                            label={translate('name')}
                                            name="name"
                                            sx={[
                                                isTextType
                                                    ? { mb: 2 }
                                                    : { mb: 0 },
                                            ]}
                                        />
                                    </Grid>

                                    {!isChoiceType && (
                                        <Grid>
                                            <Field
                                                component={ColorPicker}
                                                name={'color'}
                                                popoverPosition="bottom-end"
                                                tooltip={translate('color')}
                                            />
                                        </Grid>
                                    )}
                                </Grid>

                                {isChoiceType && <ExtraCategoryChoiceOptions />}

                                {!isCheckboxType && !isDateType && (
                                    <>
                                        <Divider sx={{ my: 2 }} />

                                        {isChoiceType && (
                                            <Field
                                                checked={values.multipleChoices}
                                                component={Switch}
                                                formControlProps={{
                                                    margin: 'none',
                                                }}
                                                label={translate(
                                                    'extraCategoriesDrawer.multipleChoices'
                                                )}
                                                name="multipleChoices"
                                            />
                                        )}

                                        <Field
                                            checked={values.filter}
                                            component={Switch}
                                            formControlProps={{
                                                margin: 'none',
                                            }}
                                            label={translate(
                                                'extraCategoriesDrawer.filter'
                                            )}
                                            name="filter"
                                        />

                                        <Field
                                            checked={values.showLabel}
                                            component={Switch}
                                            formControlProps={{
                                                margin: 'none',
                                            }}
                                            label={translate(
                                                'extraCategoriesDrawer.showLabel'
                                            )}
                                            name="showLabel"
                                        />

                                        <Divider sx={{ mt: 2 }} />
                                    </>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', mt: 4 }}>
                                <Box>
                                    <Button
                                        color="primary"
                                        disabled={
                                            isSubmitting ||
                                            disabled ||
                                            (!isSubmitting &&
                                                !disabled &&
                                                !dirty)
                                        }
                                        loading={isSubmitting}
                                        type="submit"
                                        variant="contained"
                                        onClick={(
                                            e: React.MouseEvent<HTMLButtonElement>
                                        ) => {
                                            e.preventDefault();

                                            return submitForm();
                                        }}
                                    >
                                        {buttonText}
                                    </Button>
                                </Box>

                                {onClose && (
                                    <Box sx={{ ml: 2 }}>
                                        <Button
                                            disabled={isSubmitting}
                                            variant="contained"
                                            onClick={onClose}
                                        >
                                            {translate('cancel')}
                                        </Button>
                                    </Box>
                                )}

                                {!!extraCategory && (
                                    <Box
                                        sx={{
                                            ml: 'auto',
                                        }}
                                    >
                                        <FormDeleteButton
                                            disabled={isSubmitting}
                                            onClick={() => onDelete?.()}
                                        >
                                            {translate('delete')}
                                        </FormDeleteButton>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                }}
            </Formik>
        </>
    );
};
